import { useEffect, useMemo, useState } from 'react';
import { Download, FileText, Search, X } from '@/components/icons';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { HandoffModal } from '@/components/ui/HandoffModal';
import { useAuth } from '@/auth/useAuth';
import { deleteGrn, grnExportPath, listGrns, type Grn } from '@/api/grn';
import { apiError } from '@/lib/apiError';
import { downloadExcel } from '@/lib/downloadExcel';
import { formatDate, formatINRSymbol } from '@/lib/format';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { GrnFormModal } from './GrnFormModal';
import { GrnLabelsModal } from './GrnLabelsModal';

const STOCK_TONE: Record<Grn['stockStatus'], 'warning' | 'success' | 'info'> = {
  Pending: 'warning',
  'Received in Stock': 'success',
  'Quality Checked': 'info',
};

export function GrnPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const docViewer = useDocViewer();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const presetPoId = searchParams.get('po');

  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // GRN date window — All / Date (from–to) / Month / Quarter / Year.
  const period = usePeriod();
  const { range } = period;
  useEffect(() => setPage(1), [range]);
  const [modalOpen, setModalOpen] = useState(false);
  const [defaultPoId, setDefaultPoId] = useState<number | null>(null);
  const [labelsFor, setLabelsFor] = useState<Grn | null>(null);
  const [handoffFor, setHandoffFor] = useState<Grn | null>(null);
  const { hasPerm } = useAuth();

  function onStockEntry(g: Grn) {
    // If the user can enter stock, take them straight to the Stock Entry
    // page filtered to this GRN. Otherwise forward the request to whoever
    // owns the stock module (typically Store Manager / Keeper).
    if (hasPerm('stock')) {
      navigate(`/purchase/stock?grn=${g.id}`);
    } else {
      setHandoffFor(g);
    }
  }

  // Open the modal automatically if /grn?po=<id> in URL.
  useEffect(() => {
    if (presetPoId) {
      setDefaultPoId(Number(presetPoId));
      setModalOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [presetPoId, setSearchParams]);

  const filter = useMemo(
    () => ({
      status: status || undefined,
      search: search || undefined,
      from: range?.from,
      to: range?.to,
      page,
      pageSize,
    }),
    [status, search, range, page, pageSize],
  );

  // Export every record matching the filters + search (server-side, no page limit).
  function onExport() {
    downloadExcel(grnExportPath(filter), 'GRHAMOY_GRNs.xlsx', 120_000)
      .catch((err) => toast.push(apiError(err, 'Export failed'), 'error'));
  }

  const query = useQuery({
    queryKey: ['grn', filter],
    queryFn: () => listGrns(filter),
    placeholderData: keepPreviousData,
  });

  const delMutation = useMutation({
    mutationFn: deleteGrn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grn'] });
      toast.push('GRN deleted', 'info');
    },
    onError: (e: unknown) =>
      toast.push(apiError(e, 'Delete failed'), 'error'),
  });

  async function onDelete(g: Grn) {
    if (!await confirmDlg(`Delete ${g.grnNo}?`)) return;
    delMutation.mutate(g.id);
  }

  const data = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div>
      <SectionHeader
        title="Goods Receipt Note"      />

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search"
              className="!pl-5 sm:!pl-8"
              aria-label="Search GRNs"
            />
          </div>
        </FilterField>
        <FilterField label="Stock Status">
          <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option value="Pending">Pending</option>
            <option value="Received in Stock">Received in Stock</option>
            <option value="Quality Checked">Quality Checked</option>
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        {/* Create chip lives inside the filter block on both viewports. */}
        <button
          type="button"
          onClick={() => { setDefaultPoId(null); setModalOpen(true); }}
          className="col-span-2 inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:col-span-1 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
        >
          ＋ New GRN
        </button>
        <button
          type="button"
          onClick={onExport}
          className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:self-end sm:px-3 sm:text-xs"
          title="Export every GRN matching these filters to Excel"
        >
          <Download size={12} /> Export
        </button>
      </FilterBar>

      <Table<Grn>
        title="GRN Records"
        count={`Showing ${data.length} of ${total}`}
        rows={data}
        rowKey={(r) => r.id}
        storageKey="grn"
        loading={query.isFetching && data.length === 0}
        columns={([
          { key: 'grnNo', header: 'GRN #', cell: (r) => <span className="font-extrabold text-accent">{r.grnNo}</span> },
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.grnDate) },
          { key: 'subject', header: 'Subject', width: '200px', cell: (r) => <span className="block max-w-[200px] truncate" title={r.subject ?? undefined}>{r.subject || '—'}</span> },
          {
            key: 'po',
            header: 'PO Ref',
            cell: (r) => r.po?.poNo ?? '—',
          },
          { key: 'vendor', header: 'Vendor', cell: (r) => <span className="font-bold">{r.vendor.name}</span> },
          {
            key: 'invoice',
            header: 'Invoice',
            cell: (r) => r.vendorInvoiceNo || '—',
          },
          { key: 'invoiceDate', header: 'Inv Date', cell: (r) => (r.vendorInvoiceDate ? formatDate(r.vendorInvoiceDate) : '—') },
          { key: 'vehicle', header: 'Vehicle', cell: (r) => r.vehicleNo ?? '—' },
          { key: 'items', header: 'Items', align: 'right', cell: (r) => r.items.length },
          {
            key: 'poPending',
            header: 'PO Pending',
            align: 'right',
            cell: (r) =>
              r.poPendingQty != null && r.poPendingQty > 0 ? (
                <span className="font-extrabold text-warning">{r.poPendingQty}</span>
              ) : (
                <span className="text-[var(--text-muted)]">—</span>
              ),
          },
          { key: 'value', header: 'Total', align: 'right', cell: (r) => formatINRSymbol(r.totalValue) },
          {
            key: 'stock',
            header: 'Stock Status',
            cell: (r) => (
              <div className="flex flex-nowrap items-center justify-center gap-1">
                <Badge tone={STOCK_TONE[r.stockStatus]}>{r.stockStatus}</Badge>
                {r.stockStatus === 'Pending' && (
                  <Badge
                    tone={
                      r.scanStatus === 'Verified'
                        ? 'success'
                        : r.scanStatus === 'Mismatch'
                        ? 'danger'
                        : 'info'
                    }
                  >
                    Scan: {r.scanStatus}
                  </Badge>
                )}
              </div>
            ),
          },
          {
            key: 'labels',
            header: 'QR / Barcode',
            width: '110px',
            pickerLabel: 'QR / Barcode',
            cell: (r) => (
              <Button
                variant="glass"
                size="sm"
                onClick={() => setLabelsFor(r)}
                title="Print QR / barcode labels for received units"
              >
                <span className="inline-flex items-center gap-1">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                    <rect x="5" y="5" width="3" height="3"/><rect x="16" y="5" width="3" height="3"/><rect x="5" y="16" width="3" height="3"/>
                    <path d="M14 14h3v3h-3z"/><path d="M17 17h4"/><path d="M21 14v3"/><path d="M14 17v4"/><path d="M17 21h4"/>
                  </svg>
                  Labels
                </span>
              </Button>
            ),
          },
          {
            key: 'actions',
            header: 'Action',
            width: '240px',
            cell: (r) => (
              <div className="flex flex-nowrap items-center justify-center gap-1.5">
                <Button variant="glass" size="sm" onClick={() => docViewer.open('grn', r.id, r.grnNo)} title="Open A4 document">
                  <span className="inline-flex items-center gap-1"><FileText size={14} /> Receipt</span>
                </Button>
                {r.stockStatus === 'Pending' && (
                  <>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => onStockEntry(r)}
                      title="Open Stock Entry page for this GRN"
                    >
                      + Stock
                    </Button>
                    <Button
                      variant="glass"
                      size="sm"
                      onClick={() => onDelete(r)}
                      title="Delete GRN"
                      className="!text-danger hover:!bg-danger/10"
                    >
                      <X size={14} strokeWidth={3} />
                    </Button>
                  </>
                )}
              </div>
            ),
          },
        ])}
        footer={<Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />}
      />

      <GrnFormModal open={modalOpen} defaultPoId={defaultPoId} onClose={() => setModalOpen(false)} />

      <GrnLabelsModal
        open={labelsFor !== null}
        grnId={labelsFor?.id ?? null}
        grnNo={labelsFor?.grnNo}
        onClose={() => setLabelsFor(null)}
      />

      {handoffFor && (
        <HandoffModal
          open
          onClose={() => setHandoffFor(null)}
          action="confirm stock entry for this GRN"
          payload={{
            moduleId: 'stock',
            subject: `Stock entry needed for ${handoffFor.grnNo}`,
            body: `Please scan units and confirm stock entry for GRN ${handoffFor.grnNo} (${handoffFor.vendor.name}).`,
            entityType: 'grn',
            entityId: handoffFor.id,
            actionHref: `/purchase/stock?grn=${handoffFor.id}`,
          }}
        />
      )}
    </div>
  );
}

